import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import useReveal from "../lib/useReveal";
import { SERVICE_NAMES, CONTACT } from "../lib/services";
import { submitEnquiry } from "../lib/api";
import Seo from "../components/Seo";

const initial = {
    name: "",
    email: "",
    phone: "",
    company: "",
    service_type: SERVICE_NAMES[0],
    event_date: "",
    guest_count: "",
    message: "",
};

export default function Enquiry() {
    useReveal();
    const [params] = useSearchParams();
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const svc = params.get("service");
        if (svc && SERVICE_NAMES.includes(svc)) {
            setForm((f) => ({ ...f, service_type: svc }));
        }
    }, [params]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Please enter your name";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
        if (form.phone.trim().length < 6) e.phone = "Enter a valid phone";
        if (!form.service_type) e.service_type = "Select a service";
        if (form.message.trim().length < 4) e.message = "Tell us a little more";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                guest_count: form.guest_count ? Number(form.guest_count) : null,
                event_date: form.event_date || null,
                company: form.company || null,
            };
            const res = await submitEnquiry(payload);
            setDone(res);
            toast.success("Enquiry received — we'll be in touch within one business day.");
            setForm(initial);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="enquiry-page" className="pt-40 md:pt-52">
            <Seo
                title="Get a Catering Quote — Plan Your Event | The Food Gala"
                description="Tell us about your team, event or institution. We'll architect a customised meal plan and revert within one business day. Two offices serving Cuttack, Bhubaneswar & Khorda. Call +91 82496 80443."
                path="/enquiry"
            />
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
                <div className="grid md:grid-cols-12 gap-12">
                    {/* LEFT */}
                    <aside className="md:col-span-5 reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-6">
                            Have a Gala Experience
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
                            Let's plan your
                            <br />
                            <em className="not-italic text-[#1B4332]">next gathering.</em>
                        </h1>
                        <p className="mt-6 text-lg font-light text-[#1A1A1A]/75 max-w-md leading-relaxed">
                            Tell us about your team, your event or your institution. We'll architect a
                            customised meal plan and revert within one business day.
                        </p>

                        <div className="mt-10 text-[14px] text-[#1A1A1A]/70 max-w-md leading-relaxed">
                            Prefer a call or an email? Use the details below the form, or visit
                            either of our offices listed there.
                        </div>
                    </aside>

                    {/* RIGHT - FORM */}
                    <div className="md:col-span-7 reveal">
                        {done ? (
                            <div
                                className="bg-[#FAF9F6] border border-[#1B4332]/20 p-10 md:p-14"
                                data-testid="enquiry-success-card"
                            >
                                <CheckCircle2 size={36} className="text-[#1B4332]" />
                                <h2 className="mt-6 font-display text-4xl leading-tight">Thank you, {done.name.split(" ")[0]}.</h2>
                                <p className="mt-3 text-lg font-light text-[#1A1A1A]/75">
                                    Your enquiry for <em className="text-[#1B4332] not-italic">{done.service_type}</em> has been received. Our team will reach back within one business day at <span className="text-[#1B4332]">{done.email}</span>.
                                </p>
                                <button
                                    onClick={() => setDone(null)}
                                    data-testid="enquiry-new-button"
                                    className="mt-8 inline-flex items-center gap-3 bg-[#1B4332] text-[#FAF9F6] px-7 py-3 text-[12px] uppercase tracking-[0.26em] hover:bg-[#112d21] transition-all"
                                >
                                    Send another enquiry
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                data-testid="enquiry-form"
                                className="bg-[#FAF9F6] border border-black/10 p-8 md:p-12"
                                noValidate
                            >
                                <div className="grid md:grid-cols-2 gap-x-8 gap-y-7">
                                    <Field label="Full Name" name="name" testid="enquiry-input-name" value={form.name} onChange={(v) => update("name", v)} error={errors.name} required />
                                    <Field label="Company / Organisation" name="company" testid="enquiry-input-company" value={form.company} onChange={(v) => update("company", v)} />
                                    <Field label="Email" name="email" type="email" testid="enquiry-input-email" value={form.email} onChange={(v) => update("email", v)} error={errors.email} required />
                                    <Field label="Phone" name="phone" testid="enquiry-input-phone" value={form.phone} onChange={(v) => update("phone", v)} error={errors.phone} required />

                                    <div className="flex flex-col">
                                        <Label>Service Type *</Label>
                                        <select
                                            data-testid="enquiry-input-service"
                                            value={form.service_type}
                                            onChange={(e) => update("service_type", e.target.value)}
                                            className="bg-transparent border-b border-[#1A1A1A]/25 py-3 text-[15px] focus:outline-none focus:border-[#1B4332]"
                                        >
                                            {SERVICE_NAMES.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <Field label="Number of Guests" name="guest_count" type="number" testid="enquiry-input-guests" value={form.guest_count} onChange={(v) => update("guest_count", v)} />

                                    <Field label="Event Date" name="event_date" type="date" testid="enquiry-input-date" value={form.event_date} onChange={(v) => update("event_date", v)} />

                                    <div className="md:col-span-2 flex flex-col">
                                        <Label>Tell us what you need *</Label>
                                        <textarea
                                            data-testid="enquiry-input-message"
                                            value={form.message}
                                            onChange={(e) => update("message", e.target.value)}
                                            rows={5}
                                            placeholder="Cuisine preferences, dietary requirements, budget per plate, delivery location, frequency…"
                                            className="bg-transparent border-b border-[#1A1A1A]/25 py-3 text-[15px] focus:outline-none focus:border-[#1B4332] resize-none"
                                        />
                                        {errors.message && (
                                            <span className="text-[12px] text-[#C84B31] mt-2">{errors.message}</span>
                                        )}
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="enquiry-submit"
                                    className="mt-10 inline-flex items-center gap-3 bg-[#C84B31] text-[#FAF9F6] px-8 py-4 text-[12px] uppercase tracking-[0.26em] hover:bg-[#a13a24] hover:-translate-y-[2px] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting ? "Sending…" : "Send Enquiry"}
                                    <ArrowUpRight size={16} />
                                </button>

                                <p className="mt-6 text-[12px] text-[#1A1A1A]/55">
                                    By submitting, you agree we may contact you about this enquiry.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* CONTACT + LOCATIONS — below form */}
            <section
                className="bg-[#F3F1EC] border-y border-black/5 py-20 md:py-28"
                data-testid="enquiry-contact-locations"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    {/* Phone + Email side by side */}
                    <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                        <a
                            href={`tel:${CONTACT.phone}`}
                            data-testid="enquiry-bottom-phone"
                            className="group bg-[#FAF9F6] border border-[#1B4332]/15 hover:border-[#1B4332] p-6 md:p-8 flex items-center gap-5 transition-all duration-500 hover:-translate-y-1"
                        >
                            <div className="w-12 h-12 grid place-items-center bg-[#1B4332] text-[#FAF9F6] shrink-0">
                                <Phone size={20} />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/55">Phone</div>
                                <div className="mt-1 font-display text-2xl md:text-3xl text-[#1A1A1A] truncate">
                                    {CONTACT.phone}
                                </div>
                            </div>
                        </a>
                        <a
                            href={`mailto:${CONTACT.email}`}
                            data-testid="enquiry-bottom-email"
                            className="group bg-[#FAF9F6] border border-[#1B4332]/15 hover:border-[#1B4332] p-6 md:p-8 flex items-center gap-5 transition-all duration-500 hover:-translate-y-1"
                        >
                            <div className="w-12 h-12 grid place-items-center bg-[#C84B31] text-[#FAF9F6] shrink-0">
                                <Mail size={20} />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/55">Email</div>
                                <div className="mt-1 font-display text-2xl md:text-3xl text-[#1A1A1A] break-all">
                                    {CONTACT.email}
                                </div>
                            </div>
                        </a>
                    </div>

                    {/* Office addresses */}
                    <div className="mt-10 md:mt-14 grid md:grid-cols-2 gap-8 md:gap-10">
                        {[
                            {
                                key: "head-office",
                                tag: "Head Office",
                                label: "Visit us at",
                                address: "Shankarpur, Diha Sahi, Arunodaya Market, Cuttack 753012",
                                embed: "https://www.google.com/maps?q=The%20Food%20Gala%2C%20Shankarpur%2C%20Diha%20Sahi%2C%20Arunodaya%20Market%2C%20Cuttack%20753012&output=embed",
                                openLink:
                                    "https://www.google.com/maps/search/?api=1&query=The+Food+Gala+Shankarpur+Diha+Sahi+Arunodaya+Market+Cuttack+753012",
                            },
                            {
                                key: "branch",
                                tag: "Branch",
                                label: "Branch address",
                                address: "Chhataba, Khata No 188/642, Plot No 16, Madanpur Road, Bhubaneswar, Dist: Khorda",
                                embed:
                                    "https://www.google.com/maps?q=Chhataba%2C%20Madanpur%20Road%2C%20Bhubaneswar%2C%20Khorda%2C%20Odisha&output=embed",
                                openLink:
                                    "https://www.google.com/maps/search/?api=1&query=Chhataba+Madanpur+Road+Bhubaneswar+Khorda+Odisha",
                            },
                        ].map((loc) => (
                            <div
                                key={loc.key}
                                data-testid={`enquiry-location-${loc.key}`}
                                className="bg-[#FAF9F6] border border-[#1B4332]/15 p-6 md:p-8 flex flex-col"
                            >
                                <div className="flex items-start gap-3 mb-1">
                                    <MapPin size={18} className="text-[#C84B31] mt-1 shrink-0" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/55">
                                            {loc.label}
                                        </div>
                                        <div className="mt-1 font-display text-2xl md:text-3xl leading-tight text-[#1A1A1A]">
                                            {loc.tag}
                                        </div>
                                        <div className="mt-2 text-[15px] text-[#1A1A1A]/80 leading-relaxed">
                                            {loc.address}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <div className="text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/55 mb-3">
                                        Find us on the map
                                    </div>
                                    <div className="border border-[#1B4332]/20 overflow-hidden bg-[#F3F1EC]">
                                        <iframe
                                            title={`The Food Gala — ${loc.tag}`}
                                            data-testid={`enquiry-map-${loc.key}`}
                                            src={loc.embed}
                                            width="100%"
                                            height="300"
                                            style={{
                                                border: 0,
                                                display: "block",
                                                filter: "saturate(0.92) contrast(1.02)",
                                            }}
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                            allowFullScreen
                                        />
                                    </div>
                                    <a
                                        href={loc.openLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`enquiry-map-open-${loc.key}`}
                                        className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.26em] text-[#1B4332] link-underline"
                                    >
                                        Open in Google Maps →
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}

function Label({ children }) {
    return (
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#1A1A1A]/55 mb-2">{children}</span>
    );
}

function Field({ label, name, type = "text", value, onChange, error, testid, required }) {
    return (
        <div className="flex flex-col">
            <Label>{label}{required ? " *" : ""}</Label>
            <input
                type={type}
                name={name}
                value={value}
                data-testid={testid}
                onChange={(e) => onChange(e.target.value)}
                className="bg-transparent border-b border-[#1A1A1A]/25 py-3 text-[15px] focus:outline-none focus:border-[#1B4332]"
            />
            {error && <span className="text-[12px] text-[#C84B31] mt-2">{error}</span>}
        </div>
    );
}
